/**
 * Product Q&A engine error codes (epic PI-SPEC-SW-E310, ER-1667 Package F,
 * contract ask 1).
 *
 * What the SpecTree Toolbox ask engine reports when it cannot answer a
 * question about the product. Bridge actions return them on failure and the
 * `turn-finished` event carries them; the panel maps them to plain messages
 * and keeps the person's typed question so it can be asked again. The codes
 * that mean "set something up first" carry one fixed plain sentence in
 * {@link ASK_SET_UP_FIRST_MESSAGES}: never run text, never a path or a tool
 * name.
 */
/** Every code the ask engine reports. */
export const ASK_ENGINE_ERROR_CODES = [
    "CLAUDE_NOT_INSTALLED",
    "CLAUDE_NOT_SIGNED_IN",
    /** The bundled plugin with the Product Q&A skill is missing. */
    "PLUGIN_MISSING",
    "MCP_NOT_CONNECTED",
    "NETWORK",
    /** The person's Claude usage limit is reached; the message names the reset time. */
    "USAGE_LIMIT",
    "TURN_TIMEOUT",
    /** Claude finished without a valid `SPECTREE_ASK_TURN` line for this conversation. */
    "NO_TURN_RETURNED",
    /** The person stopped the turn. */
    "STOPPED",
    /** The app is already answering a product question (one ask turn at a time per app). */
    "BUSY",
    /** A screenshot is above `ASK_IMAGE_BYTES_MAX` or not an accepted image type. */
    "IMAGE_TOO_LARGE",
    /** More than `ASK_IMAGES_MAX` screenshots. */
    "TOO_MANY_IMAGES",
    "UNKNOWN",
    /** The product's ADO project has no repositories mapped in SpecTree. */
    "REPO_NOT_MAPPED",
    /** The product's repositories are mapped but not copied to this computer yet. */
    "REPO_NOT_COPIED",
    /** The product's repository copies are being updated; try again shortly. */
    "REPOS_UPDATING",
    /** The FogBugz project is not linked to an ADO project (FogBugz ↔ ADO link, ER-1664). */
    "PRODUCT_NOT_LINKED",
    /** The person holds no role with the `ask` capability on the product's ADO project. */
    "NO_ACCESS",
    /** Claude Code would bill an API key instead of the person's subscription seat. */
    "API_KEY_BILLING",
];
/** True when `value` is an ask engine error code. */
export function isAskEngineErrorCode(value) {
    return typeof value === "string" && ASK_ENGINE_ERROR_CODES.includes(value);
}
/** The codes that mean the person (or an Admin or Project Manager) must set something up before asking. */
export const ASK_SET_UP_FIRST_CODES = [
    "CLAUDE_NOT_INSTALLED",
    "CLAUDE_NOT_SIGNED_IN",
    "PLUGIN_MISSING",
    "MCP_NOT_CONNECTED",
    "REPO_NOT_MAPPED",
    "REPO_NOT_COPIED",
    "PRODUCT_NOT_LINKED",
    "NO_ACCESS",
    "API_KEY_BILLING",
];
/** The fixed plain sentence the panel shows for each set-up code. */
export const ASK_SET_UP_FIRST_MESSAGES = {
    CLAUDE_NOT_INSTALLED: "Claude Code is not on this computer yet. Open Setup and install it first.",
    CLAUDE_NOT_SIGNED_IN: "Claude Code is not signed in. Open Setup and sign in to Claude first.",
    PLUGIN_MISSING: "The SpecTree tools for Claude are missing on this computer. Open Setup and run it again.",
    MCP_NOT_CONNECTED: "Claude cannot reach SpecTree from this computer. Open Setup and check the SpecTree step.",
    REPO_NOT_MAPPED: "This product has no code set up in SpecTree yet. Ask an Admin to add its repositories on the Project page.",
    REPO_NOT_COPIED: "This product's code is not on this computer yet. Set that repo up first: open Setup and copy the project's code.",
    PRODUCT_NOT_LINKED: "This FogBugz project is not linked to an ADO project yet. Ask your Project Manager to link it on the Project page.",
    NO_ACCESS: "You do not have a role on this product's ADO project. Ask your Project Manager to give you one.",
    API_KEY_BILLING: "Claude Code is set up to bill an API key, not your Claude subscription. Open Setup and sign in to Claude with your subscription.",
};
/** True when `code` is a set-up code with a fixed sentence in {@link ASK_SET_UP_FIRST_MESSAGES}. */
export function isAskSetUpFirstCode(code) {
    return typeof code === "string" && ASK_SET_UP_FIRST_CODES.includes(code);
}
//# sourceMappingURL=errors.js.map